import { useState, useEffect } from 'react';
import './ParentDashboard.css';

function ParentDashboard({ user, onLogout, apiBase }) {
  const [children, setChildren] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [registrationForm, setRegistrationForm] = useState({
    student_full_name: '',
    birth_date: '',
    parent_name: '',
    phone: '',
    // Level-specific fields will be added dynamically
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState('');

  const schoolLevels = [
    { code: 'tk', name: 'Taman Kanak-Kanak (TK)', icon: '🎨' },
    { code: 'sd', name: 'Sekolah Dasar (SD)', icon: '📚' },
    { code: 'smp', name: 'Sekolah Menengah Pertama (SMP)', icon: '✏️' },
    { code: 'sma', name: 'Sekolah Menengah Atas (SMA)', icon: '🎓' },
  ];

  // Fetch children data
  useEffect(() => {
    fetchChildren();
  }, []);

  async function fetchChildren() {
    try {
      setLoadingChildren(true);
      const response = await fetch(`${apiBase}/admissions/applications/mine`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const data = await response.json();
      if (response.ok && data.ok) {
        // Transform data
        const childrenList = data.data.map((app) => ({
          id: app.id,
          level_code: app.level_code,
          level_name: getLevelName(app.level_code),
          status: app.status_code,
          registered_at: app.created_at,
          form_data: JSON.parse(app.form_payload || '{}'),
        }));
        setChildren(childrenList);
      }
    } catch (err) {
      console.error('Error fetching children:', err);
    } finally {
      setLoadingChildren(false);
    }
  }

  function getLevelName(code) {
    const level = schoolLevels.find((l) => l.code === code);
    return level ? level.name : code.toUpperCase();
  }

  function handleLevelSelect(levelCode) {
    setSelectedLevel(levelCode);
    setRegistrationForm({
      student_full_name: '',
      birth_date: '',
      parent_name: user.full_name,
      phone: '',
    });
    setMessage('');
  }

  async function handleRegisterChild(e) {
    e.preventDefault();
    setSubmitLoading(true);
    setMessage('');

    try {
      const payload = {
        level_code: selectedLevel,
        form_payload: registrationForm,
      };

      const response = await fetch(`${apiBase}/admissions/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        setMessage({
          type: 'success',
          text: 'Pendaftaran anak berhasil disimpan! Menunggu review dari admin.',
        });
        setShowRegisterModal(false);
        setSelectedLevel(null);
        setTimeout(() => fetchChildren(), 1000);
      } else {
        setMessage({
          type: 'error',
          text: data.message || 'Gagal menyimpan pendaftaran',
        });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: 'Terjadi kesalahan saat mendaftar',
      });
    } finally {
      setSubmitLoading(false);
    }
  }

  function getStatusBadge(status) {
    const statuses = {
      submitted: { label: 'Submitted', color: 'blue' },
      in_review: { label: 'In Review', color: 'yellow' },
      approved: { label: 'Approved', color: 'green' },
      rejected: { label: 'Rejected', color: 'red' },
      revision_required: { label: 'Revision Needed', color: 'orange' },
    };
    const st = statuses[status] || { label: status, color: 'gray' };
    return (
      <span className={`status-badge status-${st.color}`}>{st.label}</span>
    );
  }

  return (
    <div className="parent-dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <h1>📚 Dashboard Orang Tua</h1>
          <p className="header-subtitle">Kelola data dan pendaftaran anak Anda</p>
        </div>
        <div className="header-right">
          <div className="user-info">
            <p className="user-name">{user.full_name}</p>
            <p className="user-email">{user.email}</p>
          </div>
          <button onClick={onLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        {/* Messages */}
        {message && (
          <div className={`message-alert message-${message.type}`}>
            {message.text}
            <button
              className="close-message"
              onClick={() => setMessage('')}
            >
              ✕
            </button>
          </div>
        )}

        {/* Children List Section */}
        <section className="section">
          <div className="section-header">
            <h2>📋 Daftar Anak Terdaftar</h2>
            <button
              className="btn-primary"
              onClick={() => setShowRegisterModal(true)}
            >
              + Daftarkan Anak Baru
            </button>
          </div>

          {loadingChildren ? (
            <div className="loading">Memuat data...</div>
          ) : children.length === 0 ? (
            <div className="empty-state">
              <p>Belum ada anak yang terdaftar</p>
              <button
                className="btn-primary"
                onClick={() => setShowRegisterModal(true)}
              >
                Daftarkan Anak Pertama
              </button>
            </div>
          ) : (
            <div className="children-table">
              <table>
                <thead>
                  <tr>
                    <th>Nama Anak</th>
                    <th>Tingkat Sekolah</th>
                    <th>Status</th>
                    <th>Tanggal Daftar</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {children.map((child) => (
                    <tr key={child.id}>
                      <td className="cell-name">
                        {child.form_data.student_full_name || '-'}
                      </td>
                      <td>{child.level_name}</td>
                      <td>{getStatusBadge(child.status)}</td>
                      <td>
                        {new Date(child.registered_at).toLocaleDateString(
                          'id-ID'
                        )}
                      </td>
                      <td>
                        <button className="btn-small btn-info">Lihat</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Quick Stats */}
        <section className="section">
          <h2>📊 Ringkasan</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{children.length}</div>
              <div className="stat-label">Anak Terdaftar</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {children.filter((c) => c.status === 'approved').length}
              </div>
              <div className="stat-label">Sudah Diterima</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {children.filter((c) => c.status === 'submitted').length}
              </div>
              <div className="stat-label">Menunggu Review</div>
            </div>
          </div>
        </section>
      </div>

      {/* Register Modal */}
      {showRegisterModal && (
        <div className="modal-overlay" onClick={() => setShowRegisterModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {!selectedLevel ? (
              // Level Selection
              <div className="modal-level-select">
                <h2>Pilih Tingkat Sekolah</h2>
                <p className="modal-subtitle">
                  Pilih tingkat pendidikan yang ingin didaftarkan
                </p>

                <div className="level-grid">
                  {schoolLevels.map((level) => (
                    <button
                      key={level.code}
                      className="level-card"
                      onClick={() => handleLevelSelect(level.code)}
                    >
                      <div className="level-icon">{level.icon}</div>
                      <div className="level-text">{level.name}</div>
                    </button>
                  ))}
                </div>

                <button
                  className="btn-secondary"
                  onClick={() => setShowRegisterModal(false)}
                >
                  Batal
                </button>
              </div>
            ) : (
              // Registration Form
              <div className="modal-registration">
                <div className="modal-header">
                  <h2>Daftarkan Anak - {getLevelName(selectedLevel)}</h2>
                  <button
                    className="close-modal"
                    onClick={() => {
                      setShowRegisterModal(false);
                      setSelectedLevel(null);
                    }}
                  >
                    ✕
                  </button>
                </div>

                <form onSubmit={handleRegisterChild} className="registration-form">
                  <div className="form-group">
                    <label>Nama Anak</label>
                    <input
                      type="text"
                      value={registrationForm.student_full_name}
                      onChange={(e) =>
                        setRegistrationForm({
                          ...registrationForm,
                          student_full_name: e.target.value,
                        })
                      }
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Tanggal Lahir</label>
                    <input
                      type="date"
                      value={registrationForm.birth_date}
                      onChange={(e) =>
                        setRegistrationForm({
                          ...registrationForm,
                          birth_date: e.target.value,
                        })
                      }
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Nama Orang Tua/Wali</label>
                    <input
                      type="text"
                      value={registrationForm.parent_name}
                      onChange={(e) =>
                        setRegistrationForm({
                          ...registrationForm,
                          parent_name: e.target.value,
                        })
                      }
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Nomor Telepon</label>
                    <input
                      type="tel"
                      value={registrationForm.phone}
                      onChange={(e) =>
                        setRegistrationForm({
                          ...registrationForm,
                          phone: e.target.value,
                        })
                      }
                      required
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setSelectedLevel(null)}
                      disabled={submitLoading}
                    >
                      Kembali
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={submitLoading}
                    >
                      {submitLoading ? 'Menyimpan...' : 'Daftar Anak'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ParentDashboard;
